import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';

const DeliveryConfirmationModal = ({ isOpen, onClose, order, onConfirmStart }) => {
  const [showItems, setShowItems] = useState(false);

  const handleStartDelivery = () => {
    onConfirmStart(order.id);
    onClose();
  };

  // Helper para obtener el monto correcto según el endpoint usado
  const getOrderAmount = (order) => {
    // Para mensajeros, el campo se llama 'total'
    return parseFloat(order.total || order.total_amount || 0);
  };

  const getDeliveryMethodLabel = (method) => {
    const labels = {
      domicilio_ciudad: 'Domicilio Ciudad',
      domicilio_nacional: 'Domicilio Nacional',
      recogida_tienda: 'Recogida en Tienda',
      envio_nacional: 'Envío Nacional',
      envio_internacional: 'Envío Internacional',
      contraentrega: 'Contraentrega'
    };
    return labels[method] || method || 'No especificado';
  };

  const getPaymentMethodLabel = (method) => {
    const map = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cliente_credito: 'Crédito',
      pago_electronico: 'Pago Electrónico',
      contraentrega: 'Contraentrega',
      publicidad: 'Publicidad',
      reposicion: 'Reposición'
    };
    const key = (method || '').toLowerCase();
    return map[key] || method || 'No especificado';
  };

  const getShippingPaymentLabel = (method) => {
    const map = {
      contado: 'Pagado',
      paid: 'Pagado',
      pagado: 'Pagado',
      contraentrega: 'Por Cobrar (Contraentrega)',
      por_cobrar: 'Por Cobrar'
    };
    const key = (method || '').toLowerCase();
    return map[key] || method || 'No especificado';
  };

  // Helper robusto para dirección: toma el primer campo no vacío y filtra placeholders
  const getOrderAddress = (o) => {
    const toText = (v) => {
      if (v == null) return '';
      const s = String(v).trim();
      const low = s.toLowerCase();
      if (!s || ['undefined', 'null', 'n/a', 'na', 'sin direccion', 'sin dirección', 'no aplica'].includes(low)) return '';
      return s;
    };
    const candidates = [
      o?.shipping_address,
      o?.delivery_address,
      o?.customer_address,
      o?.address,
      o?.customerAddress,
      o?.shippingAddress,
      o?.deliveryAddress
    ];
    for (const c of candidates) {
      const s = toText(c);
      if (s) return s;
    }
    return '';
  };

  // Normaliza método de pago cuando no viene:
  // 1) Si el envío es contraentrega/por_cobrar, mostrar "contraentrega"
  // 2) Si hay payment_amount o siigo_balance, asumir "contraentrega"
  const normalizedShippingPay = (order?.shipping_payment_method || '').toLowerCase();
  const normalizedPaymentMethod = order?.payment_method
    || (['contraentrega', 'por_cobrar'].includes(normalizedShippingPay) ? 'contraentrega' : null)
    || ((Number(order?.payment_amount ?? 0) > 0 || Number(order?.siigo_balance ?? 0) > 0) ? 'contraentrega' : null)
    || (((Number(order?.total_amount ?? order?.total ?? 0) - Number(order?.paid_amount ?? order?.amount_paid ?? 0)) > 0) ? 'contraentrega' : null);

  const itemsCount = Array.isArray(order?.items) ? order.items.length : (order?.items_count ?? order?.itemsCount ?? 0);
  const amountsBase = computeCollectionAmounts(order);

  // Fallback defensivo (ajustado):
  // SOLO aplicar cuando exista un saldo explícito de pago mixto (payment_amount/siigo_balance > 0).
  // Evita forzar cobros por "total - pagado" cuando el método es transferencia y no se requiere pago.
  const safeAmounts = (() => {
    const paid = Number(order?.paid_amount ?? order?.amount_paid ?? 0);
    const paymentAmt = Number(order?.payment_amount ?? order?.siigo_balance ?? 0);

    if (!isCreditOrder(order) && amountsBase.totalDue === 0 && paymentAmt > 0) {
      const productDue = Math.max(paymentAmt - paid, 0);
      const shippingDue = amountsBase.shippingDue;
      return { productDue, shippingDue, totalDue: productDue + shippingDue };
    }
    return amountsBase;
  })();




  if (!isOpen || !order) return null;

  const { totalDue, productDue, shippingDue } = safeAmounts;
  const isCredit = isCreditOrder(order);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[95vh] border border-gray-200">

        {/* Header - Simple & Clean */}
        <div className="bg-white px-6 py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-100">
              <Icons.Navigation className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">
                Confirmar Inicio de Entrega
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                Pedido <span className="text-blue-600 font-bold">#{order.order_number}</span> • {itemsCount} ítems
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/30">

          {/* Main Delivery Info Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden text-sm">
            <div className="bg-gray-50/50 border-b border-gray-100 px-4 py-2.5">
              <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center">
                <Icons.MapPin className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Información de Entrega
              </h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-4">
                <div className="bg-blue-50/40 p-3 rounded-lg border border-blue-100/50">
                  <p className="text-[10px] font-bold text-blue-600 uppercase mb-1 tracking-tight">Dirección Principal</p>
                  <p className="text-base font-bold text-gray-900 leading-snug">
                    {getOrderAddress(order) || 'No especificada'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Cliente</p>
                  <div className="flex items-center space-x-2 mt-0.5">
                    <p className="text-gray-900 font-bold">{order.customer_name || order.client_name}</p>
                    <a href={`tel:${order.customer_phone || order.client_phone}`} className="p-1.5 bg-green-50 text-green-600 rounded-md hover:bg-green-100 border border-green-100 transition-colors">
                      <Icons.Phone className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-xs text-gray-500 font-medium">{order.customer_phone || order.client_phone}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50/50 border border-transparent hover:border-gray-100">
                  <Icons.Truck className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Envío</p>
                    <p className="text-xs text-gray-900 font-bold">{getDeliveryMethodLabel(order.delivery_method)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50/50 border border-transparent hover:border-gray-100">
                  <Icons.CreditCard className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Pago</p>
                    <p className="text-xs text-gray-900 font-bold">{getPaymentMethodLabel(normalizedPaymentMethod)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50/50 border border-transparent hover:border-gray-100">
                  <Icons.Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Fecha</p>
                    <p className="text-xs text-gray-900 font-bold">
                      {order.shipping_date
                        ? format(new Date(order.shipping_date), "EEEE, d 'de' MMMM", { locale: es })
                        : 'No especificada'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Center - Money to Collect */}
          <div className={`bg-white rounded-xl overflow-hidden border shadow-sm ${totalDue > 0 ? 'border-red-200' : 'border-green-200'
            }`}>
            <div className={`px-4 py-2 flex items-center justify-between border-b ${totalDue > 0 ? 'bg-red-50/50 border-red-100' : 'bg-green-50/50 border-green-100'
              }`}>
              <div className={`flex items-center space-x-2 ${totalDue > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {totalDue > 0 ? <Icons.AlertCircle className="w-4 h-4" /> : <Icons.CheckCircle2 className="w-4 h-4" />}
                <h3 className="font-bold text-[11px] uppercase tracking-wider">
                  {totalDue > 0 ? 'Monto a Recaudar' : (isCredit ? 'Entrega a Crédito' : 'Ya Pagado')}
                </h3>
              </div>
              {totalDue > 0 && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-black uppercase">Cobrar</span>}
            </div>

            <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex flex-col">
                <div className="flex items-baseline space-x-2">
                  <span className={`text-4xl font-black ${totalDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${totalDue.toLocaleString('es-CO')}
                  </span>
                  <span className="text-gray-400 text-sm font-bold tracking-tighter">COP</span>
                </div>
                <p className={`mt-1 font-bold text-[11px] uppercase tracking-wide ${totalDue > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {totalDue > 0
                    ? 'Recaudar este valor al entregar'
                    : (isCredit ? 'Saldo a favor o cupo de crédito' : 'Todo saldado previamente')}
                </p>
              </div>

              {totalDue > 0 && (
                <div className="w-full md:w-56 space-y-1.5 pt-4 md:pt-0 border-t md:border-t-0 border-gray-50">
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="text-gray-500 font-medium">Subtotal:</span>
                    <span className="font-bold text-gray-700">${productDue.toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between items-center text-[11px] pb-1.5 border-b border-gray-100">
                    <span className="text-gray-500 font-medium">Envío:</span>
                    <span className="font-bold text-gray-700">${shippingDue.toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="font-black text-gray-800 text-[11px]">TOTAL</span>
                    <span className="font-black text-red-600 text-base">${totalDue.toLocaleString('es-CO')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          {(order.delivery_notes || order.special_instructions) && (
            <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
              <div className="flex items-center space-x-2 text-amber-700 mb-2">
                <Icons.StickyNote className="w-4 h-4" />
                <h4 className="text-xs font-bold uppercase tracking-wider">Notas de Mensajería</h4>
              </div>
              <div className="space-y-1.5 text-amber-900 text-[13px]">
                {order.delivery_notes && (
                  <p className="leading-relaxed"><span className="font-bold">Entrega:</span> {order.delivery_notes}</p>
                )}
                {order.special_instructions && (
                  <p className="leading-relaxed"><span className="font-bold">Especiales:</span> {order.special_instructions}</p>
                )}
              </div>
            </div>
          )}

          {/* Product List Section - Fixed Dropdown */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <button
              onClick={() => setShowItems(!showItems)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center space-x-2">
                <Icons.Package className="w-4 h-4 text-blue-600" />
                <span className="font-bold text-gray-700 text-sm">Resumen de Contenido</span>
                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-black border border-blue-200">{itemsCount}</span>
              </div>
              <Icons.ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-300 ${showItems ? 'rotate-180' : ''}`} />
            </button>

            {showItems && (
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 px-4 pb-2 animate-in slide-in-from-top-2 duration-200">
                {Array.isArray(order.items) && order.items.map((item, index) => (
                  <div key={index} className="py-2.5 flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <h4 className="font-bold text-gray-900 truncate text-sm">
                        {item.product_name || item.name}
                      </h4>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">REF: {item.product_code || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-gray-600">
                        {item.quantity} x ${((item.unit_price ?? item.price ?? 0)).toLocaleString('es-CO')}
                      </p>
                      <p className="text-xs font-black text-blue-600 mt-0.5">
                        ${(item.quantity * (item.unit_price ?? item.price ?? 0))?.toLocaleString('es-CO')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-500 font-bold text-sm hover:bg-gray-100 rounded-xl transition-all border border-transparent"
          >
            Regresar
          </button>
          <button
            onClick={handleStartDelivery}
            className="flex-1 md:flex-none px-10 py-3 bg-blue-600 text-white font-black text-sm rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center space-x-2 shadow-lg shadow-blue-100"
          >
            <Icons.Play className="w-4 h-4 fill-current" />
            <span>INICIAR ENTREGA</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryConfirmationModal;
